import * as React from "react";
import {
  fetchAvAssessmentAnswers,
  fetchAvAssessmentQuestions,
  fetchAvailabilityIndexResult,
  fetchEmployeeIdByAuthUserId,
  finalizeEventKontrolScores,
  getCurrentUser,
  markAvAssessmentQuestionSkipped,
  recalculateEmployeeAvi,
  unmarkAvAssessmentQuestionSkipped,
  upsertAvAssessmentAnswer,
} from "../../api/queries";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useI18n } from "../../i18n/I18nProvider";
import {
  SkippedQuestionBadge,
  SkippedQuestionNotice,
  skipQuestionButtonSx,
} from "../SkippedQuestionStatus";
import {
  AssessmentHeader,
  AssessmentSectionIntro,
  AssessmentSidebar,
  answerGridSx,
  assessmentContentSx,
  assessmentLayoutSx,
  assessmentOuterSx,
  assessmentOutlineButtonSx,
  assessmentPageSx,
  assessmentPanelSx,
  assessmentPrimaryButtonSx,
  getAnswerOptionSx,
  questionBodySx,
  questionCardSx,
  questionHeaderSx,
  questionLabelSx,
} from "../AssessmentTestLayout";
import type {
  AvAssessmentAnswer,
  AvAssessmentOption,
  AvAssessmentQuestion,
  EmployeeInfo,
} from "../../types/scores";

const AV_SECTIONS = [
  { key: "presence", labelEn: "Presence", labelDe: "Anwesenheit", feeds: ["Presence"] },
  { key: "claims", labelEn: "Competing claims", labelDe: "Konkurrierende Pflichten", feeds: ["Competing claims"] },
  { key: "role", labelEn: "Role clarity", labelDe: "Rollenklarheit", feeds: ["Role clarity"] },
];

function getOptionLabel(option: AvAssessmentOption, lang: "en" | "de") {
  return lang === "de" ? option.label_de || option.label_en : option.label_en || option.label_de;
}

export default function AvailabilityAssessment(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;
  const i18n: any = useI18n();
  const lang: "en" | "de" = i18n.lang ?? "en";
  const L = React.useCallback((en: string, de: string) => (lang === "de" ? de : en), [lang]);

  const UI = React.useMemo(
    () => ({
      headerTitle: L("Availability Assessment", "Verfügbarkeits-Assessment"),
      headerSubtitle: L(
        "Breakfast availability and role clarity questions.",
        "Breakfast-Fragen zu Verfügbarkeit und Rollenklarheit."
      ),
      loading: L("Loading questions...", "Fragen werden geladen..."),
      noQuestions: L("No availability questions found.", "Keine Verfügbarkeitsfragen gefunden."),
      sidebarTitle: L("Your progress", "Dein Fortschritt"),
      sidebarSections: L("Sections", "Abschnitte"),
      sidebarOverall: (done: number, total: number) =>
        L(`${done} of ${total} questions done`, `${done} von ${total} Fragen erledigt`),
      sidebarRemaining: (remaining: number) =>
        remaining === 0
          ? L("All questions completed", "Alle Fragen abgeschlossen")
          : L(
              `${remaining} question${remaining === 1 ? "" : "s"} left`,
              `Noch ${remaining} ${remaining === 1 ? "Frage" : "Fragen"} offen`
            ),
      sidebarCount: (done: number, total: number) => `${done}/${total}`,
      sidebarSkipped: (skipped: number) => L(`${skipped} skipped`, `${skipped} übersprungen`),
      sidebarEmpty: L("Nothing to show yet.", "Noch nichts anzuzeigen."),
      btnBack: L("Previous", "Zurück"),
      btnFinish: L("Finish", "Abschließen"),
      btnSkip: L("Skip question", "Frage überspringen"),
      skippedLabel: L("Skipped", "Übersprungen"),
      skippedNotice: L(
        "You can return to this question later if you want a more complete availability picture.",
        "Du kannst später zu dieser Frage zurückkehren, wenn du ein vollständigeres Bild deiner Verfügbarkeit möchtest."
      ),
      sectionTitle: L("Availability Index", "Availability Index"),
      sectionSubtitle: L(
        "These questions help you understand how reachable, mobile and available you may be if your organisation needs people during a disruption.",
        "Diese Fragen helfen dir einzuschätzen, wie erreichbar, mobil und verfügbar du sein könntest, wenn deine Organisation während einer Störung auf Mitarbeitende angewiesen ist."
      ),
      qLabel: (n: number) => L(`Question ${n}`, `Frage ${n}`),
      errNotAuth: L("Not authenticated. Please log in again.", "Nicht angemeldet. Bitte erneut einloggen."),
      errEmpNotFound: L("Employee record not found for this account.", "Kein Mitarbeiterprofil für dieses Konto gefunden."),
      errLoadQuestions: L("Failed to load availability questions.", "Verfügbarkeitsfragen konnten nicht geladen werden."),
      errLoadAnswers: L("Failed to load previous answers.", "Vorherige Antworten konnten nicht geladen werden."),
      errSave: L("Failed to save your answer. Please try again.", "Antwort konnte nicht gespeichert werden. Bitte erneut versuchen."),
      errSkip: L("Failed to skip this question. Please try again.", "Frage konnte nicht übersprungen werden. Bitte erneut versuchen."),
    }),
    [L]
  );

  const [employee, setEmployee] = React.useState<EmployeeInfo | null>(null);
  const [questions, setQuestions] = React.useState<AvAssessmentQuestion[]>([]);
  const [answers, setAnswers] = React.useState<Record<string, string>>({});
  const [skippedQuestionCodes, setSkippedQuestionCodes] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [savingQuestionId, setSavingQuestionId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [finishing, setFinishing] = React.useState(false);

  const isAnswered = React.useCallback(
    (q: AvAssessmentQuestion) => Boolean(answers[q.id]),
    [answers]
  );

  const isSkipped = React.useCallback(
    (q: AvAssessmentQuestion) => skippedQuestionCodes.includes(q.question_code),
    [skippedQuestionCodes]
  );

  const completedTotal = questions.filter((q) => isAnswered(q) || isSkipped(q)).length;

  const sidebarSections = React.useMemo(
    () =>
      AV_SECTIONS.map((section) => {
        const bucket = questions.filter((q) =>
          section.feeds.some((feed) => q.feeds.includes(feed))
        );
        return {
          key: section.key,
          label: lang === "de" ? section.labelDe : section.labelEn,
          answered: bucket.filter(isAnswered).length,
          skipped: bucket.filter((q) => !isAnswered(q) && isSkipped(q)).length,
          total: bucket.length,
        };
      }).filter((section) => section.total > 0),
    [questions, lang, isAnswered, isSkipped]
  );

  React.useEffect(() => {
    let mounted = true;

    async function loadAll() {
      setLoading(true);
      setError(null);

      try {
        const { data: userData, error: userErr } = await getCurrentUser();
        if (userErr || !userData?.user) throw new Error(UI.errNotAuth);

        const { data: empRow, error: empErr } =
          await fetchEmployeeIdByAuthUserId(userData.user.id);

        if (empErr) throw empErr;
        if (!empRow) throw new Error(UI.errEmpNotFound);

        const { data: qRows, error: qErr } = await fetchAvAssessmentQuestions();
        if (qErr) throw new Error(UI.errLoadQuestions);

        const { data: aRows, error: aErr } = await fetchAvAssessmentAnswers(empRow.id);
        if (aErr) throw new Error(UI.errLoadAnswers);

        const { data: aviRow } = await fetchAvailabilityIndexResult(empRow.id);

        const answerMap: Record<string, string> = {};
        for (const row of (aRows ?? []) as AvAssessmentAnswer[]) {
          answerMap[row.question_id] = row.given_answer;
        }

        if (!mounted) return;
        setEmployee({ id: empRow.id });
        setQuestions(qRows ?? []);
        setAnswers(answerMap);
        setSkippedQuestionCodes(((aviRow as any)?.av_skipped ?? []) as string[]);
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message ?? UI.errLoadQuestions);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    }

    loadAll();
    return () => {
      mounted = false;
    };
  }, [UI.errNotAuth, UI.errEmpNotFound, UI.errLoadQuestions, UI.errLoadAnswers]);

  async function saveAnswer(question: AvAssessmentQuestion, givenAnswer: string) {
    if (!employee) return;

    setSavingQuestionId(question.id);
    setError(null);

    const previousAnswer = answers[question.id];
    const previousSkipped = skippedQuestionCodes;

    setAnswers((old) => ({ ...old, [question.id]: givenAnswer }));
    setSkippedQuestionCodes((old) => old.filter((code) => code !== question.question_code));

    const { error: saveErr } = await upsertAvAssessmentAnswer(employee.id, question.id, givenAnswer);

    if (saveErr) {
      setAnswers((old) => {
        const next = { ...old };
        if (previousAnswer) next[question.id] = previousAnswer;
        else delete next[question.id];
        return next;
      });
      setSkippedQuestionCodes(previousSkipped);
      setSavingQuestionId(null);
      setError(UI.errSave);
      return;
    }

    const { error: unskipErr } = await unmarkAvAssessmentQuestionSkipped(employee.id, question.question_code);
    if (unskipErr) {
      setSkippedQuestionCodes(previousSkipped);
      setError(UI.errSave);
    }

    setSavingQuestionId(null);
  }

  async function skipQuestion(question: AvAssessmentQuestion) {
    if (!employee) return;

    setSavingQuestionId(question.id);
    setError(null);

    const previousAnswer = answers[question.id];
    const previousSkipped = skippedQuestionCodes;
    const nextSkipped = Array.from(new Set([...previousSkipped, question.question_code]));

    setAnswers((old) => {
      const next = { ...old };
      delete next[question.id];
      return next;
    });
    setSkippedQuestionCodes(nextSkipped);

    const { error: skipErr } = await markAvAssessmentQuestionSkipped(employee.id, question.question_code);

    setSavingQuestionId(null);
    if (skipErr) {
      setAnswers((old) => {
        const next = { ...old };
        if (previousAnswer) next[question.id] = previousAnswer;
        return next;
      });
      setSkippedQuestionCodes(previousSkipped);
      setError(UI.errSkip);
    }
  }

  async function handleFinish() {
    if (employee) {
      setFinishing(true);
      // Best-effort: makes sure all three scores and the availability index
      // are snapshotted for the current event even if nothing was just
      // (re)answered. A failure here shouldn't block navigation, but
      // shouldn't be silent either.
      const [{ error: finalizeErr }, { error: aviErr }] = await Promise.all([
        finalizeEventKontrolScores(employee.id),
        recalculateEmployeeAvi(employee.id),
      ]);
      if (finalizeErr) {
        console.error("finalize_event_kontrol_scores failed", finalizeErr);
      }
      if (aviErr) {
        console.error("recalculate_employee_avi failed", aviErr);
      }
      setFinishing(false);
    }

    handlePageChange("KontrolScore");
  }

  return (
    <Box sx={assessmentPageSx}>
      <Box sx={assessmentOuterSx}>
        <AssessmentHeader
          icon={<EventAvailableOutlinedIcon />}
          title={UI.headerTitle}
          subtitle={UI.headerSubtitle}
        />

        <Box sx={assessmentLayoutSx}>
          <AssessmentSidebar
            title={UI.sidebarTitle}
            sections={sidebarSections}
            completed={completedTotal}
            total={questions.length}
            overallLabel={UI.sidebarOverall}
            remainingLabel={UI.sidebarRemaining}
            sectionsHeading={UI.sidebarSections}
            countLabel={UI.sidebarCount}
            skippedLabel={UI.sidebarSkipped}
            emptyLabel={loading ? UI.loading : UI.sidebarEmpty}
          />

          <Paper elevation={0} sx={assessmentPanelSx}>
            <Box sx={assessmentContentSx}>
              <Stack spacing={2.25}>
                <AssessmentSectionIntro
                  eyebrow="AVI"
                  title={UI.sectionTitle}
                  subtitle={UI.sectionSubtitle}
                />

                {loading ? (
                  <Typography sx={{ fontWeight: 900 }}>{UI.loading}</Typography>
                ) : error ? (
                  <Alert severity="error">{error}</Alert>
                ) : questions.length === 0 ? (
                  <Alert severity="info">{UI.noQuestions}</Alert>
                ) : (
                  <Stack spacing={2}>
                    {questions.map((question, index) => (
                      <AvQuestionCard
                        key={question.id}
                        question={question}
                        qLabel={UI.qLabel(index + 1)}
                        selectedAnswer={answers[question.id] ?? ""}
                        skipped={skippedQuestionCodes.includes(question.question_code)}
                        lang={lang}
                        saving={savingQuestionId === question.id}
                        btnSkip={UI.btnSkip}
                        skippedLabel={UI.skippedLabel}
                        skippedNotice={UI.skippedNotice}
                        onChange={(value) => saveAnswer(question, value)}
                        onSkip={() => skipQuestion(question)}
                      />
                    ))}
                  </Stack>
                )}

                <Stack direction="row" justifyContent="space-between" sx={{ pt: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => handlePageChange("EKStest")}
                    disabled={loading || Boolean(savingQuestionId)}
                    sx={assessmentOutlineButtonSx}
                  >
                    {UI.btnBack}
                  </Button>

                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    disabled={loading || Boolean(savingQuestionId) || finishing}
                    onClick={handleFinish}
                    sx={assessmentPrimaryButtonSx}
                  >
                    {UI.btnFinish}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}

function AvQuestionCard(props: {
  question: AvAssessmentQuestion;
  selectedAnswer: string;
  skipped: boolean;
  lang: "en" | "de";
  saving: boolean;
  qLabel: string;
  btnSkip: string;
  skippedLabel: string;
  skippedNotice: string;
  onChange: (value: string) => void;
  onSkip: () => void;
}) {
  const {
    question,
    selectedAnswer,
    skipped,
    lang,
    saving,
    qLabel,
    btnSkip,
    skippedLabel,
    skippedNotice,
    onChange,
    onSkip,
  } = props;

  return (
    <Paper elevation={0} sx={questionCardSx}>
      <Box sx={questionHeaderSx}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography sx={questionLabelSx}>{qLabel}</Typography>
          {skipped && <SkippedQuestionBadge label={skippedLabel} />}
        </Stack>
        <Typography sx={{ mt: 1, fontWeight: 900, color: "#111827", fontSize: 18 }}>
          {lang === "de" ? question.question_de : question.question_en}
        </Typography>
      </Box>

      <Box sx={questionBodySx}>
        <RadioGroup value={selectedAnswer} onChange={(event) => onChange(event.target.value)}>
          <Box sx={answerGridSx}>
            {question.answer_options.map((option) => {
              const selected = selectedAnswer === option.value;
              return (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={getOptionLabel(option, lang)}
                  disabled={saving}
                  sx={getAnswerOptionSx(selected, saving)}
                />
              );
            })}
          </Box>
        </RadioGroup>

        {skipped && <SkippedQuestionNotice label={skippedLabel} notice={skippedNotice} />}

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.25 }}>
          <Button onClick={onSkip} disabled={saving || skipped} sx={skipQuestionButtonSx}>
            {btnSkip}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}
